import { useState, useEffect } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { v4 as uuidv4 } from 'uuid';
import CustomerPhotoCapture from "@/components/customers/CustomerPhotoCapture";
import { uploadFile, getBranches } from "@/integrations/supabase/services";
import { zodResolver } from "@hookform/resolvers/zod";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { Bike, Branch } from "@/types/models";
import { useQuery } from "@tanstack/react-query";

const formSchema = z.object({
  number: z.coerce.number().min(1, "El número de bicicleta es requerido"),
  type: z.string().min(1, "El tipo es requerido"),
  model: z.string().min(1, "El modelo es requerido"),
  color: z.string().min(1, "El color es requerido"),
  branch_id: z.string().min(1, "La sucursal es requerida"),
  size: z.coerce.number().optional(),
  serial: z.string().optional(),
  photo_url: z.string().optional(),
});

type BikeFormValues = z.infer<typeof formSchema>;

interface BikeFormProps {
  bike?: Bike;
  onSuccess: () => void;
}

const BikeForm = ({ bike, onSuccess }: BikeFormProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [photoDataUrl, setPhotoDataUrl] = useState<string | null>(null);

  // Fetch branches for the select dropdown
  const { data: branches } = useQuery({
    queryKey: ['branches'],
    queryFn: getBranches,
  });

  const form = useForm<BikeFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      number: bike?.number || 0,
      type: bike?.type || "",
      model: bike?.model || "",
      color: bike?.color || "",
      branch_id: bike?.branch_id || "",
      size: bike?.size || undefined,
      serial: bike?.serial || "",
      photo_url: bike?.photo_url || "",
    },
  });

  useEffect(() => {
    if (bike) {
      form.setValue('photo_url', bike.photo_url || "");
      form.setValue('branch_id', bike.branch_id);
      form.setValue('size', bike.size);
      form.setValue('serial', bike.serial || "");
      // Set existing photo for preview
      setPhotoDataUrl(bike.photo_url || null);
    }
  }, [bike, form]);

  const onSubmit = async (data: BikeFormValues) => {
    setIsSubmitting(true);
    setError("");
    
    let uploadedPhotoUrl: string | undefined = bike?.photo_url;

    try {
      // Only upload if we have a new photo (base64 data)
      if (photoDataUrl && photoDataUrl.startsWith('data:')) {
        const filePath = `bikes/${uuidv4()}.jpg`;
        uploadedPhotoUrl = await uploadFile(photoDataUrl, filePath);
        form.setValue('photo_url', uploadedPhotoUrl);
      } else if (photoDataUrl) {
        // Use existing photo URL
        uploadedPhotoUrl = photoDataUrl;
      }

      if (bike) {
        // Actualizar bicicleta existente
        const { error: updateError } = await supabase
          .from("bikes")
          .update({
            number: data.number,
            type: data.type,
            model: data.model,
            color: data.color,
            branch_id: data.branch_id,
            size: data.size || null,
            serial: data.serial || null,
            photo_url: uploadedPhotoUrl,
          })
          .eq("id", bike.id);

        if (updateError) throw updateError;
      } else {
        // Crear nueva bicicleta
        const { error: insertError } = await supabase
          .from("bikes")
          .insert({
            number: data.number,
            type: data.type,
            model: data.model,
            color: data.color,
            branch_id: data.branch_id,
            size: data.size || null,
            serial: data.serial || null,
            photo_url: uploadedPhotoUrl,
          });

        if (insertError) throw insertError;
      }

      onSuccess();
    } catch (err) {
      console.error("Error al guardar la bicicleta:", err);
      setError("Hubo un error al guardar la bicicleta. Por favor, intente nuevamente.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form {...form}>
      <ScrollArea className="h-[600px]">
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="number"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Número de Bicicleta</FormLabel>
                <FormControl>
                  <Input 
                    type="number" 
                    placeholder="Número único de la bicicleta" 
                    {...field}
                    onChange={(e) => field.onChange(Number(e.target.value))}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="branch_id"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Sucursal</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecciona una sucursal" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {branches?.map((branch: Branch) => (
                      <SelectItem key={branch.id} value={branch.id}>
                        {branch.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="type"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Tipo</FormLabel>
                <FormControl>
                  <Input placeholder="Tipo de bicicleta" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="model"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Modelo</FormLabel>
                <FormControl>
                  <Input placeholder="Modelo" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="color"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Color</FormLabel>
                <FormControl>
                  <Input placeholder="Color" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="size"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Tamaño</FormLabel>
                <FormControl>
                  <Input 
                    type="number" 
                    placeholder="Tamaño de la bicicleta" 
                    {...field}
                    onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="serial"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Serial</FormLabel>
                <FormControl>
                  <Input placeholder="Número de serie" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="space-y-2">
            <CustomerPhotoCapture
              label="Foto de la Bicicleta"
              onCapture={setPhotoDataUrl}
              currentImage={photoDataUrl}
            />
            {/* Hidden FormField to satisfy Zod schema */}
            <FormField
              control={form.control}
              name="photo_url"
              render={({ field }) => (
                <FormItem className="hidden">
                  <FormControl>
                    <Input type="hidden" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <div className="flex justify-end gap-2 pt-4">
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Guardando..." : bike ? "Actualizar" : "Crear"}
            </Button>
          </div>
        </form>
      </ScrollArea>
    </Form>
  );
};

export default BikeForm;
